import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { T } from "../EditContext";
import { Heading } from "./Sections";
import { accentCard } from "../accent";
import { useCart } from "../../context/CartContext";
import { formatINR } from "../../lib/constants";

const BASE = process.env.REACT_APP_BACKEND_URL;

// "Works best with…" / "Take together for best results" — links real products from the CRM.
export const ProductPairing = ({ props: p = {} }) => {
    const { addItem } = useCart();
    const { data: products = [] } = useQuery({
        queryKey: ["pairing-products"],
        queryFn: async () => (await fetch(`${BASE}/api/products`)).json(),
    });
    const bySlug = Object.fromEntries(products.map((x) => [x.slug, x]));
    const items = (p.items || []).filter((i) => i.visible !== false);
    const a = accentCard(p.accent || "gold", p.accent_hex);
    const slugs = items.map((i) => i.slug).filter(Boolean).join(",");
    const { data: bundle } = useQuery({
        queryKey: ["bundle-match", slugs],
        queryFn: async () => (await fetch(`${BASE}/api/site/bundle-match?slugs=${encodeURIComponent(slugs)}`)).json(),
        enabled: slugs.split(",").length > 1,
    });
    if (!items.length) return null;

    const addBundle = () => {
        const rows = (bundle?.items || []).map((it) => bySlug[it.slug]).filter(Boolean);
        if (!rows.length) return;
        rows.forEach((prod) => addItem(prod, 1));
        toast.success(`${bundle.name} added — the bundle saving is applied at checkout`);
    };

    return (
        <section className="mx-auto max-w-7xl px-6 md:px-12 py-10 md:py-14" data-testid="product-pairing">
            <T f="eyebrow" value={p.eyebrow || "Better together"} as="div" className="eyebrow mb-3" />
            <Heading f="heading" value={p.heading || "Works best with"} />
            <T f="body" value={p.body} as="p" multiline className="mt-4 text-zinc-500 max-w-3xl" />

            {p.combination && (
                <div className="mt-6 inline-flex items-center gap-2 border px-4 py-2.5"
                    style={{ borderColor: `${a.hex}66`, background: `${a.hex}12` }}
                    data-testid="pairing-combination">
                    <Sparkles className="w-3.5 h-3.5" style={{ color: a.hex }} />
                    <span className="mono text-[11px] tracking-[0.16em]" style={{ color: a.hex }}>{p.combination}</span>
                </div>
            )}

            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {items.map((item, i) => {
                    const prod = bySlug[item.slug] || {};
                    const name = item.title || prod.name || item.slug;
                    const image = item.image_url || prod.images?.[0];
                    return (
                        <Link key={item.slug || i} to={`/product/${item.slug}`} data-testid={`pairing-card-${i}`}
                            className={`${a.className} p-6 block hover:-translate-y-1`} style={a.style}>
                            <span aria-hidden className="sci-rule" />
                            {image && (
                                <div className="h-40 flex items-center justify-center mb-5">
                                    <img src={image} alt={name} className="max-h-full object-contain product-media__img" />
                                </div>
                            )}
                            {item.tag && <div className="mono text-[9px] tracking-[0.2em]" style={{ color: a.hex }}>{item.tag.toUpperCase()}</div>}
                            <h3 className="font-display text-xl uppercase tracking-tight mt-2">{name}</h3>
                            {item.desc && <p className="text-sm text-zinc-500 mt-2 leading-relaxed">{item.desc}</p>}
                            <span className="mt-5 inline-flex items-center gap-2 mono text-[10px] tracking-[0.2em]" style={{ color: a.hex }}>
                                VIEW PRODUCT <ArrowRight className="w-3.5 h-3.5" />
                            </span>
                        </Link>
                    );
                })}
            </div>

            {bundle?.id && bundle.discount > 0 && (
                <div className="mt-8 border p-6 flex flex-wrap items-center justify-between gap-5"
                    style={{ borderColor: `${a.hex}66`, background: `${a.hex}0f` }}
                    data-testid="bundle-offer">
                    <div>
                        <div className="mono text-[9px] tracking-[0.2em]" style={{ color: a.hex }}>
                            BUY THEM TOGETHER
                        </div>
                        <div className="font-display text-2xl uppercase tracking-tight mt-1">{bundle.name}</div>
                        <div className="mt-2 flex items-baseline gap-3">
                            <span className="font-display text-2xl" data-testid="bundle-price">
                                {formatINR(bundle.bundle_price)}
                            </span>
                            <span className="text-zinc-500 line-through text-sm">{formatINR(bundle.list_price)}</span>
                            <span className="mono text-[10px] tracking-[0.16em]" style={{ color: a.hex }}>
                                SAVE {formatINR(bundle.discount)}
                            </span>
                        </div>
                        {bundle.note && <p className="mt-2 text-sm text-zinc-500 max-w-xl">{bundle.note}</p>}
                    </div>
                    <button type="button" onClick={addBundle} data-testid="bundle-add-to-cart"
                        className="btn-pill btn-primary">ADD BUNDLE TO CART</button>
                </div>
            )}
        </section>
    );
};

export default ProductPairing;
